import { useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { useMyProfile } from '@/entities/user';
import {
  PolicyAcceptanceModal,
  usePolicyAcceptanceModal,
} from '@/features/policy-acceptance';
import RouteErrorBoundary from '@/shared/ui/error/RouteErrorBoundary';
import ErrorStatePage from '@/pages/ErrorState/ErrorStatePage';
import { resetCircuitBreakers } from '@/shared/api';
import { translateBackendError } from '@/shared/lib/errors';

interface PolicyAcceptanceProviderProps {
  children: React.ReactNode;
}

/**
 * Mounts the PolicyAcceptanceModal in the app tree and automatically opens it
 * when the authenticated user's profile has pending policy acceptances.
 *
 * This provider must be rendered inside QueryClientProvider (so useMyProfile
 * can run) and should wrap the RouterProvider so the modal overlays all routes.
 *
 * **Validates: Requirements 1, 5**
 */
export function PolicyAcceptanceProvider({ children }: PolicyAcceptanceProviderProps) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: isAuthLoading, logout } = useAuth0();
  const {
    data: profileData,
    isLoading: isProfileLoading,
    isError: isProfileError,
    error: profileError,
    refetch: refetchProfile,
  } = useMyProfile();
  const { isOpen, open } = usePolicyAcceptanceModal();
  const pending = useMemo(
    () => profileData?.data?.pendingPolicyAcceptances ?? [],
    [profileData?.data?.pendingPolicyAcceptances],
  );
  const profile = profileData?.data;
  const isFirstTimeOnboarding =
    profile?.user?.isSuperAdmin !== true &&
    (profile?.onboarding == null || profile.onboarding.isCompleted === false);

  useEffect(() => {
    // profileData shape from /auth/profile:
    // { data: { pendingPolicyAcceptances: [...] } }
    if (isAuthenticated && pending.length > 0 && !isOpen) {
      open('profile', pending as import('@/entities/policy').PendingPolicyAcceptance[]);
    }
  }, [isAuthenticated, pending, isOpen, open]);

  // `&& !isProfileError` is load-bearing, not defensive noise.
  //
  // Unmounting `children` unmounts the whole router, and with it every
  // `useMyProfile()` observer in the app. React Query treats a query with no
  // data as stale, so remounting refetches it — and refetching a data-less
  // query puts `status` back to "loading", which unmounts the tree again.
  // Mount → fetch → loading → unmount → mount. With a profile that can never
  // succeed (user row deleted while the Auth0 session lived on) that ran at
  // roughly 600 requests a second behind a blank white page, and
  // RequirePermission's error branch never got to render because the router
  // was torn down before it could.
  //
  // Once the query has errored, the answer is in: stop holding, and let the
  // failure surface below.
  const isCheckingAuthenticatedProfile =
    isAuthenticated && isProfileLoading && !isProfileError;
  // A first-time vendor's onboarding controller is mounted inside `children`.
  // Keep it out of the tree while acceptance is pending/open so its fixed
  // full-screen overlay cannot cover this modal. Once acceptance succeeds and
  // the refreshed profile has no pending policies, onboarding mounts normally.
  // Completed vendors keep the existing behaviour (the app remains mounted
  // behind policy acceptance).
  const isPrioritizingFirstLoginAcceptance =
    isFirstTimeOnboarding && (pending.length > 0 || isOpen);
  const shouldHoldApplication =
    isAuthLoading ||
    isCheckingAuthenticatedProfile ||
    isPrioritizingFirstLoginAcceptance;

  // A profile that will not load is a dead end, not a slow load: no route can
  // render without it, and every remount asks for it again. Say so, and give
  // the vendor a way out — a blank page offers neither an explanation nor a
  // logout, which is what a deleted user actually hit.
  //
  // Sits above the router deliberately. RequirePermission has its own copy of
  // this branch, but it only covers routes beneath it, and it never got to run
  // while the tree was being torn down.
  if (isAuthenticated && isProfileError) {
    const status = (profileError as { response?: { status?: number } })?.response
      ?.status;
    // Through translateBackendError, not the raw `response.data.message`: it
    // resolves `errors.<CODE>` first, so STORE_MISMATCH reads as a sentence a
    // vendor can act on rather than the API's "Store mismatch." — and it is
    // the only path that reaches ne/hi.
    const description = translateBackendError(profileError, t);

    // 401/403 mean the session itself is the problem, so signing out is the
    // only action that can help; ErrorStatePage renders Sign out INSTEAD of
    // Go-to-dashboard + Try again, which is right here — offering "Try again"
    // against a deterministic 403 just invites the merchant to keep pressing.
    //
    // A 5xx is the opposite: transient, and worth retrying. Passing no
    // onLogout lets the page show its normal Try again, and that retry clears
    // the breakers first — an open circuit would otherwise reject it with no
    // request at all, a button that visibly does nothing.
    const isSessionFailure = status === 401 || status === 403;

    return (
      <ErrorStatePage
        statusCode={status ?? 500}
        description={description}
        onLogout={
          isSessionFailure
            ? () => logout({ logoutParams: { returnTo: window.location.origin } })
            : undefined
        }
        onRetry={
          isSessionFailure
            ? undefined
            : () => {
                resetCircuitBreakers();
                void refetchProfile();
              }
        }
      />
    );
  }

  return (
    <>
      {!shouldHoldApplication && children}
      <RouteErrorBoundary>
        <PolicyAcceptanceModal />
      </RouteErrorBoundary>
    </>
  );
}
